import { useState, type CSSProperties, type KeyboardEvent } from 'react';

import { FieldLabel } from './FieldLabel';

/** Pendant zu `SfTextBox`. */
export interface TextBoxProps {
  value: string | null;
  onChange: (value: string) => void;
  /** Feste Beschriftung über dem Feld (REQ-24 §2). */
  label?: string;
  /** Zusatz in schwächerer Schrift hinter der Beschriftung. */
  hint?: string;
  /** Nur für echte Beispielwerte — nicht als Ersatz für `label` (REQ-24 §3.2). */
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  style?: CSSProperties;
  title?: string;
}

export function TextBox(props: TextBoxProps) {
  const { onChange } = props;
  const [focused, setFocused] = useState(false);
  // Zwischenstand während der Eingabe; `null` heißt „nicht in Bearbeitung“.
  // `onChange` feuert wie bei Syncfusion erst beim `change`-Ereignis (Blur/Enter).
  const [draft, setDraft] = useState<string | null>(null);

  const value = props.value ?? '';
  const text = draft ?? value;

  const commit = () => {
    setDraft(null);
    if (text !== value) onChange(text);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') commit();
    if (event.key === 'Escape') setDraft(null);
  };

  return (
    <FieldLabel
      id={props.id}
      className={props.className}
      style={props.style}
      title={props.title}
      label={props.label}
      hint={props.hint}
      focused={focused}
      disabled={props.disabled}
    >
      {({ id }) => (
        <input
          id={id}
          type="text"
          className="e-input"
          value={text}
          placeholder={props.placeholder}
          readOnly={props.readOnly === true}
          disabled={props.disabled === true}
          onChange={(event) => {
            setDraft(event.target.value);
          }}
          onFocus={() => {
            setFocused(true);
          }}
          onBlur={() => {
            setFocused(false);
            commit();
          }}
          onKeyDown={onKeyDown}
        />
      )}
    </FieldLabel>
  );
}

export default TextBox;
